/** 协议 §5.6.3：6 路天线配置（2 输入 + 4 输出） */

import type { AmplifierLocaleKey } from '../../i18n/amplifier/locales';

export const ANTENNA_CONFIG_LENGTH = 6;

export const MIN_ANTENNA_FREQ_HZ = 1_800_000;

export type AntennaLabelKey =
  | 'input1'
  | 'input2'
  | 'output1'
  | 'output2'
  | 'output3'
  | 'output4';

export const ANTENNA_LABEL_TO_INDEX: Record<AntennaLabelKey, number> = {
  input1: 0,
  input2: 1,
  output1: 2,
  output2: 3,
  output3: 4,
  output4: 5,
};

export interface AntennaConfig {
  names: string[];
  freqStartHz: number[];
  freqEndHz: number[];
}

export function createDefaultAntennaConfig(): AntennaConfig {
  return {
    names: Array(ANTENNA_CONFIG_LENGTH).fill(''),
    freqStartHz: Array(ANTENNA_CONFIG_LENGTH).fill(MIN_ANTENNA_FREQ_HZ),
    freqEndHz: Array(ANTENNA_CONFIG_LENGTH).fill(130_000_000),
  };
}

export function resolveAntennaDisplayName(
  index: number,
  config: AntennaConfig | undefined,
  fallbackLabel: string,
): string {
  const name = config?.names[index]?.trim();
  return name || fallbackLabel;
}

/** 将设备上报的 Hz 数值原样转为输入框展示字符串 */
export function hzToInput(hz: number): string {
  if (!Number.isFinite(hz)) return '';
  return String(hz);
}

export function parseHzInput(hzText: string): number | null {
  const trimmed = hzText.trim();
  if (!trimmed) return null;
  const hz = Number(trimmed);
  if (!Number.isFinite(hz) || hz < 0) return null;
  return Math.round(hz);
}

export interface AntennaFreqValidationResult {
  ok: boolean;
  messageKey?: AmplifierLocaleKey;
  messageParams?: Record<string, string | number>;
  freqStartHz?: number;
  freqEndHz?: number;
}

export function validateAntennaFreqRangeHz(
  startHzText: string,
  endHzText: string,
): AntennaFreqValidationResult {
  const freqStartHz = parseHzInput(startHzText);
  const freqEndHz = parseHzInput(endHzText);

  if (freqStartHz === null || freqEndHz === null) {
    return { ok: false, messageKey: '请输入有效的频率（Hz）' };
  }
  if (freqStartHz < MIN_ANTENNA_FREQ_HZ) {
    return { ok: false, messageKey: '起始频率不能低于 {min} Hz', messageParams: { min: MIN_ANTENNA_FREQ_HZ } };
  }
  if (freqEndHz < MIN_ANTENNA_FREQ_HZ) {
    return { ok: false, messageKey: '结束频率不能低于 {min} Hz', messageParams: { min: MIN_ANTENNA_FREQ_HZ } };
  }
  if (freqStartHz > freqEndHz) {
    return { ok: false, messageKey: '起始频率不能大于结束频率' };
  }

  return { ok: true, freqStartHz, freqEndHz };
}

export function patchAntennaConfigAtIndex(
  config: AntennaConfig,
  index: number,
  patch: {
    name?: string;
    freqStartHz?: number;
    freqEndHz?: number;
  },
): AntennaConfig {
  const names = [...config.names];
  const freqStartHz = [...config.freqStartHz];
  const freqEndHz = [...config.freqEndHz];

  if (patch.name !== undefined) {
    names[index] = patch.name;
  }
  if (patch.freqStartHz !== undefined) {
    freqStartHz[index] = patch.freqStartHz;
  }
  if (patch.freqEndHz !== undefined) {
    freqEndHz[index] = patch.freqEndHz;
  }

  return { names, freqStartHz, freqEndHz };
}

export function isValidAntennaConfigArrays(
  names: unknown,
  freqStart: unknown,
  freqEnd: unknown,
): boolean {
  return (
    Array.isArray(names)
    && Array.isArray(freqStart)
    && Array.isArray(freqEnd)
    && names.length === ANTENNA_CONFIG_LENGTH
    && freqStart.length === ANTENNA_CONFIG_LENGTH
    && freqEnd.length === ANTENNA_CONFIG_LENGTH
  );
}

export function parseAntennaConfigFromRemote(
  names: unknown,
  freqStart: unknown,
  freqEnd: unknown,
  previous?: AntennaConfig,
): AntennaConfig | undefined {
  if (!isValidAntennaConfigArrays(names, freqStart, freqEnd)) {
    return previous;
  }

  const nameList = names as unknown[];
  const freqStartList = freqStart as unknown[];
  const freqEndList = freqEnd as unknown[];

  return {
    names: nameList.map((item) => (item == null ? '' : String(item))),
    freqStartHz: freqStartList.map((item) => {
      const n = Number(item);
      return Number.isFinite(n) ? n : MIN_ANTENNA_FREQ_HZ;
    }),
    freqEndHz: freqEndList.map((item) => {
      const n = Number(item);
      return Number.isFinite(n) ? n : 130_000_000;
    }),
  };
}
